import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Modal, Form, Row, Col, Spinner } from 'react-bootstrap';
import { Tag, Plus, Edit2, Power } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const blank = { code:'', discount_type:'percentage', discount_value:'', max_uses:'', expires_at:'', trial_days:0, description:'' };

const PromoCodesPage = () => {
    const [codes,   setCodes]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal,   setModal]   = useState({ show:false, editing:null });
    const [form,    setForm]    = useState(blank);
    const [saving,  setSaving]  = useState(false);

    const fetchCodes = async () => {
        setLoading(true);
        try { const res = await axiosInstance.get('/superadmin/promo-codes'); setCodes(res.data.data||[]); }
        catch { toast.error('Failed to load promo codes'); }
        finally { setLoading(false); }
    };
    useEffect(() => { fetchCodes(); }, []);

    const openCreate = () => { setModal({ show:true, editing:null }); setForm(blank); };
    const openEdit   = (c) => { setModal({ show:true, editing:c }); setForm({ ...c, expires_at: c.expires_at?.slice(0,10)||'' }); };

    const handleSave = async () => {
        if (!form.code || !form.discount_value) { toast.warn('Code and discount value required'); return; }
        setSaving(true);
        try {
            if (modal.editing) {
                await axiosInstance.put(`/superadmin/promo-codes/${modal.editing.id}`, form);
                toast.success('Promo code updated');
            } else {
                await axiosInstance.post('/superadmin/promo-codes', form);
                toast.success('Promo code created');
            }
            setModal({ show:false }); fetchCodes();
        } catch (err) { toast.error(err.response?.data?.message||'Save failed'); }
        finally { setSaving(false); }
    };

    const handleToggle = async (id, isActive) => {
        try {
            await axiosInstance.put(`/superadmin/promo-codes/${id}`, { is_active: !isActive });
            toast.success(`Promo code ${!isActive ? 'activated' : 'deactivated'}`);
            fetchCodes();
        } catch { toast.error('Failed'); }
    };

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="fw-bold mb-1"><Tag size={22} className="me-2 text-primary"/>Promo Codes</h4>
                    <p className="text-muted small mb-0">Create discount codes for new signups</p>
                </div>
                <Button variant="primary" onClick={openCreate} className="d-flex align-items-center gap-2">
                    <Plus size={18}/>New Code
                </Button>
            </div>

            <Card className="border-0 shadow-sm">
                {loading ? <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div> : (
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize:11 }}>
                                <th className="ps-4">Code</th><th>Type</th><th>Discount</th><th>Trial Days</th>
                                <th>Max Uses</th><th>Used</th><th>Expires</th><th>Status</th><th className="text-end pe-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {codes.length > 0 ? codes.map(c => (
                                <tr key={c.id}>
                                    <td className="ps-4"><code className="fw-bold">{c.code}</code></td>
                                    <td className="text-capitalize">{c.discount_type}</td>
                                    <td className="fw-bold text-success">{c.discount_type==='percentage' ? `${c.discount_value}%` : `₹${c.discount_value}`}</td>
                                    <td>{c.trial_days > 0 ? `${c.trial_days} days` : '—'}</td>
                                    <td>{c.max_uses || '∞'}</td>
                                    <td>{c.used_count || 0}</td>
                                    <td className="text-muted">{c.expires_at ? new Date(c.expires_at).toLocaleDateString('en-IN') : '—'}</td>
                                    <td><Badge bg={c.is_active ? 'success' : 'secondary'}>{c.is_active ? 'Active' : 'Inactive'}</Badge></td>
                                    <td className="text-end pe-4">
                                        <Button size="sm" variant="link" className="text-muted p-1 me-1" onClick={() => openEdit(c)}><Edit2 size={15}/></Button>
                                        <Button size="sm" variant="link" className={c.is_active ? 'text-danger p-1' : 'text-success p-1'} onClick={() => handleToggle(c.id, c.is_active)}>
                                            <Power size={15}/>
                                        </Button>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan={9} className="text-center py-5 text-muted">No promo codes yet. Create one to offer discounts on signup.</td></tr>
                            )}
                        </tbody>
                    </Table>
                )}
            </Card>

            <Modal show={modal.show} onHide={() => setModal({show:false})} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold">{modal.editing ? 'Edit Promo Code' : 'New Promo Code'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="g-3">
                        {!modal.editing && (
                            <Col xs={12}>
                                <Form.Label className="small fw-bold">Code *</Form.Label>
                                <Form.Control placeholder="e.g. LAUNCH50" style={{ textTransform:'uppercase', fontWeight:700, letterSpacing:'1px' }}
                                    value={form.code} onChange={e=>setForm(p=>({...p,code:e.target.value.toUpperCase()}))}/>
                                <Form.Text className="text-muted">Customers enter this on signup. Auto-uppercased.</Form.Text>
                            </Col>
                        )}
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Discount Type</Form.Label>
                            <Form.Select value={form.discount_type} onChange={e=>setForm(p=>({...p,discount_type:e.target.value}))}>
                                <option value="percentage">Percentage (%)</option>
                                <option value="flat">Flat Amount (₹)</option>
                            </Form.Select>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Discount Value *</Form.Label>
                            <Form.Control type="number" min="1" placeholder={form.discount_type==='percentage'?'e.g. 20':'e.g. 500'}
                                value={form.discount_value} onChange={e=>setForm(p=>({...p,discount_value:e.target.value}))}/>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Free Trial Days</Form.Label>
                            <Form.Control type="number" min="0" max="90" placeholder="0 = no trial"
                                value={form.trial_days} onChange={e=>setForm(p=>({...p,trial_days:parseInt(e.target.value)||0}))}/>
                            <Form.Text className="text-muted">Only promo holders get free trial.</Form.Text>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Max Uses</Form.Label>
                            <Form.Control type="number" min="1" placeholder="Leave blank = unlimited"
                                value={form.max_uses} onChange={e=>setForm(p=>({...p,max_uses:e.target.value}))}/>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Expires On</Form.Label>
                            <Form.Control type="date" value={form.expires_at} onChange={e=>setForm(p=>({...p,expires_at:e.target.value}))}/>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Internal Description</Form.Label>
                            <Form.Control placeholder="e.g. For launch event attendees" value={form.description}
                                onChange={e=>setForm(p=>({...p,description:e.target.value}))}/>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={()=>setModal({show:false})}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving}>{saving?<Spinner size="sm"/>:modal.editing?'Save Changes':'Create Code'}</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PromoCodesPage;
